import { useCallback } from 'react'
import { backendTurnError, decodeTurnFrame, rayIdFromHeaders } from '../engine/errors'
import type { ChatRequestBody } from '../engine/services'
import { streamTurnFrames } from '../engine/sse'
import { ChatError } from '../types'
import type { components as BackendOpenApiComponents } from '../types/backend-openapi'
import { useChatApi } from './use-chat-api'

export type { ChatRequestBody }

type ChatEventCallback = (event: BackendOpenApiComponents['schemas']['ChatSSEStreamEvent']) => Promise<void> | void

/**
 * Legacy raw-stream submitter: POST a chat request and hand every parsed SSE
 * event to the callback, with no store writes. The real submit path runs
 * through the turn engine (`useSubmitMessage`); this remains only for the
 * onboarding DummyChat's ephemeral suggestion request.
 */
export const useSubmitChatRequest = () => {
  const api = useChatApi()

  return useCallback(
    async (
      chatRequestBody: ChatRequestBody,
      { localConversationId }: { accessToken?: string; localConversationId: string },
      onEvent: ChatEventCallback,
    ) => {
      const errorChatId = chatRequestBody.conversationId ?? localConversationId
      const { signal } = new AbortController()
      const connected = await api.startTurnStream(errorChatId, chatRequestBody, signal)
      if (connected.isErr()) {
        throw ChatError.fromTurnError(connected.error)
      }

      const response = connected.value
      if (!response.ok) {
        throw ChatError.fromTurnError(
          backendTurnError(
            { phase: 'submit', chatId: errorChatId },
            { kind: 'error-response', message: `HTTP ${response.status}`, rayId: rayIdFromHeaders(response.headers) },
          ),
        )
      }

      for await (const frame of streamTurnFrames(response, signal)) {
        const decoded = decodeTurnFrame({ phase: 'submit', chatId: errorChatId }, frame.data)
        if (decoded.isErr()) continue
        await onEvent(decoded.value as BackendOpenApiComponents['schemas']['ChatSSEStreamEvent'])
      }
    },
    [api],
  )
}
